import { useEffect, useRef } from "react";
import type { ArcadeSpotSpec } from "../../scenario/types";
import type { ArcadeSpotPin } from "../../three/ArcadeMarkers";
import { arcadeScenarioId, useArcadeProgress } from "./results";
import { spotLabel, spotState } from "./hub";
import "./arcade.css";

/** Click labels under the hovering arrows of every playable arcade spot. */
export function HubPins({
  spots,
  pins,
  onPick,
}: {
  spots: ArcadeSpotSpec[];
  pins: Record<string, ArcadeSpotPin>;
  onPick: (spotId: string) => void;
}) {
  const progress = useArcadeProgress(arcadeScenarioId());
  const live = useRef({ spots, progress, onPick });
  live.current = { spots, progress, onPick };

  // The bold link in the guide card ("Switch on the TV") works from anywhere in
  // the hall: the labels live outside the device layer, so the guide's own
  // click search cannot find them and sends this event instead.
  useEffect(() => {
    const on = (e: Event) => {
      const want = String((e as CustomEvent).detail ?? "").trim().toLowerCase();
      if (!want) return;
      const { spots: list, progress: p, onPick: pick } = live.current;
      for (const s of list) {
        const st = spotState(s, p, true);
        if (st !== "hidden" && spotLabel(s, st, p).toLowerCase() === want) {
          pick(s.id);
          return;
        }
      }
    };
    window.addEventListener("cf-guide-action", on);
    return () => window.removeEventListener("cf-guide-action", on);
  }, []);

  return (
    <>
      {spots.map((s) => {
        const st = spotState(s, progress, true);
        const pin = pins[s.id];
        if (st === "hidden" || !pin?.visible) return null;
        return (
          <button
            key={s.id}
            type="button"
            className={`arc-hubpin${st === "done" ? " is-done" : ""}`}
            style={{ left: `${pin.xPct}%`, top: `${pin.yPct}%` }}
            onClick={() => onPick(s.id)}
          >
            <span className="arc-hubpin__dot" />
            <span className="arc-hubpin__label">{spotLabel(s, st, progress)}</span>
          </button>
        );
      })}
    </>
  );
}
